/**
 * Financial Dashboard – data from GET /api/overview (all‑time earnings, last withdrawal, linked account + monthly bar chart).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useAuth } from './Auth/AuthContext';
import { fetchOverview } from '../utils/shopifyApi';
import '../styles/dashboard.css';

function formatMoney(amount, currency = 'USD') {
    if (amount == null || Number.isNaN(amount)) return `${currency} 0.00`;
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
}

function EarningsTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    return (
        <div
            className="dashboard-chart-tooltip"
            style={{
                padding: '8px 12px',
                background: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                border: '1px solid #E2E8F0',
                color: '#111827'
            }}
        >
            <div className="tooltip-date" style={{ marginBottom: 4 }}>Day {d.day}</div>
            <div className="tooltip-row">
                <span>Earnings: <strong>{formatMoney(d.earnings)}</strong></span>
            </div>
        </div>
    );
}

export default function FinancialDashboard() {
    const { tokens } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const result = await fetchOverview({ accessToken: tokens?.accessToken });
        if (result.ok && result.data) {
            setData(result.data);
        } else {
            setError(result.error || 'Failed to load overview');
        }
        setLoading(false);
    }, [tokens?.accessToken]);

    useEffect(() => {
        load();
    }, [load]);

    const currency = data?.currency || 'USD';
    const kpis = data
        ? [
            { label: 'All-Time Earnings', value: formatMoney(data.allTimeEarnings, currency), accent: '#22c55e' },
            { label: 'Last Withdrawal', value: formatMoney(data.lastWithdrawal, currency), accent: '#000000' },
            { label: 'Linked Account', value: data.linkedAccount || 'Not linked', accent: '#38bdf8' }
        ]
        : [];

    const monthlyEarnings = data?.monthlyEarnings ?? [];

    if (loading && !data) {
        return (
            <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px', fontFamily: "'Inter', sans-serif" }}>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>Overview</div>
                <h1 style={{ fontSize: '1.8rem', fontWeight: 700, margin: 0, color: 'var(--dash-text)' }}>Financial Dashboard</h1>
                <div style={{ marginTop: '2rem', fontSize: '15px', color: '#6b7280' }}>Loading…</div>
            </div>
        );
    }

    if (error && !data) {
        return (
            <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px', fontFamily: "'Inter', sans-serif" }}>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>Overview</div>
                <h1 style={{ fontSize: '1.8rem', fontWeight: 700, margin: 0, color: 'var(--dash-text)' }}>Financial Dashboard</h1>
                <div style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '15px', color: '#ef4444' }}>{error}</span>
                    <button
                        type="button"
                        onClick={load}
                        style={{
                            padding: '8px 20px',
                            backgroundColor: '#111827',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: 500,
                            cursor: 'pointer'
                        }}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            style={{
                maxWidth: '1400px',
                margin: '0 auto',
                padding: '24px',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
            }}
        >
            {/* Breadcrumb */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                color: '#666',
                marginBottom: '16px'
            }}>
                <span>Overview</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.6rem' }}>
                <h1 style={{ fontSize: '1.8rem', fontWeight: 700, margin: 0, color: 'var(--dash-text)' }}>
                    Financial Dashboard
                </h1>
                <button
                    type="button"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 20px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#37352f',
                        cursor: 'pointer',
                        transition: '0.15s'
                    }}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14M5 12h14"/>
                    </svg>
                    Withdraw Funds
                </button>
            </div>

            <div style={{ background: 'transparent', borderRadius: 0, border: 'none', padding: '1.6rem 0 0 0', boxShadow: 'none' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '1rem', marginBottom: '1.6rem' }}>
                    {kpis.map((kpi) => (
                        <div
                            key={kpi.label}
                            style={{
                                background: '#ffffff',
                                borderRadius: '12px',
                                padding: '1.5rem 1.8rem',
                                border: '1px solid var(--dash-border-light)',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                minHeight: '104px',
                                boxShadow: 'var(--dash-shadow)'
                            }}
                        >
                            <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--dash-text-muted)', marginBottom: '0.45rem' }}>
                                {kpi.label}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                <span style={{ fontSize: '2rem', fontWeight: 700, color: kpi.accent }}>
                                    {kpi.value}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>

                <div
                    style={{
                        background: '#ffffff',
                        borderRadius: '14px',
                        border: '1px solid var(--dash-border-light)',
                        padding: '1.8rem',
                        marginTop: '1.4rem',
                        boxShadow: 'var(--dash-shadow)'
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.8rem' }}>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--dash-text)', margin: 0 }}>
                            This Month&apos;s Earnings
                        </h2>
                        <span style={{ fontSize: '0.9rem', color: 'var(--dash-text-soft)' }}>vs. Previous Month</span>
                    </div>
                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={monthlyEarnings} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--dash-border-light)"/>
                            <XAxis dataKey="day" stroke="var(--dash-text-muted)" fontSize={11} tickLine={false} />
                            <YAxis
                                stroke="var(--dash-text-muted)"
                                fontSize={11}
                                tickLine={false}
                                axisLine={false}
                                tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v}`)}
                            />
                            <Tooltip content={<EarningsTooltip />} />
                            <Bar dataKey="earnings" radius={[4, 4, 0, 0]} fill="#22c55e" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}

